import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  CircleNotch,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Scales,
  DownloadSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import ReportToolbar from '../../components/ReportToolbar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const accountTypeInfo = {
  asset: { label: 'Asset', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  liability: { label: 'Liability', color: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  equity: { label: 'Equity', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  income: { label: 'Income', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  expense: { label: 'Expense', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
};

export default function TrialBalancePage() {
  const { hasPermission } = useAuth();
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchTrialBalance();
  }, []);

  const fetchTrialBalance = async (date = null) => {
    setLoading(true);
    try {
      const params = date ? `?as_of_date=${date}` : '';
      const response = await axios.get(`${API_URL}/reports/trial-balance${params}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load trial balance');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    fetchTrialBalance(asOfDate);
  };

  // Group items by account type
  const groupedItems = data?.items?.reduce((acc, item) => {
    if (!acc[item.account_type]) {
      acc[item.account_type] = [];
    }
    acc[item.account_type].push(item);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="trial-balance-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-4">
          <Link to="/dashboard/accounts">
            <Button variant="ghost" size="icon">
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
              Trial Balance
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
              Summary of all account balances
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm">As of:</Label>
            <Input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-40"
              data-testid="trial-balance-date"
            />
            <Button variant="outline" onClick={handleDateChange} data-testid="refresh-btn">
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Universal export toolbar */}
      <ReportToolbar
        testidPrefix="tb-toolbar"
        downloadBaseName={`trial-balance-${asOfDate}`}
        exportUrl={(fmt) => `${API_URL}/reports/trial-balance.${fmt}?as_of_date=${asOfDate}`}
      />

      {/* Balance Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`p-4 rounded-lg flex items-center gap-3 ${
          data?.is_balanced 
            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' 
            : 'bg-red-500/10 text-red-700 dark:text-red-400'
        }`}
      >
        {data?.is_balanced ? (
          <>
            <CheckCircle size={24} weight="fill" />
            <span className="font-medium">Trial Balance is in balance</span>
          </>
        ) : (
          <>
            <XCircle size={24} weight="fill" />
            <span className="font-medium">
              Trial Balance is out of balance by {formatCurrency(Math.abs((data?.total_debit || 0) - (data?.total_credit || 0)))}
            </span>
          </>
        )}
      </motion.div>

      {/* Trial Balance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scales size={20} className="text-primary" weight="duotone" />
              <span className="font-medium">Trial Balance as of {data?.as_of_date}</span>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Code</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Account Name</th>
                <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Debit</th>
                <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {/* Assets */}
              {groupedItems.asset?.length > 0 && (
                <>
                  <tr className="bg-blue-500/5">
                    <td colSpan={4} className="px-6 py-2 font-medium text-blue-700 dark:text-blue-400">
                      Assets
                    </td>
                  </tr>
                  {groupedItems.asset.map(item => (
                    <tr key={item.account_id} className="hover:bg-muted/30">
                      <td className="px-6 py-3 pl-10 font-mono text-sm">{item.account_code}</td>
                      <td className="px-6 py-3">{item.account_name}</td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                      </td>
                    </tr>
                  ))}
                </>
              )}

              {/* Liabilities */}
              {groupedItems.liability?.length > 0 && (
                <>
                  <tr className="bg-red-500/5">
                    <td colSpan={4} className="px-6 py-2 font-medium text-red-700 dark:text-red-400">
                      Liabilities
                    </td>
                  </tr>
                  {groupedItems.liability.map(item => (
                    <tr key={item.account_id} className="hover:bg-muted/30">
                      <td className="px-6 py-3 pl-10 font-mono text-sm">{item.account_code}</td>
                      <td className="px-6 py-3">{item.account_name}</td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                      </td>
                    </tr>
                  ))}
                </>
              )}

              {/* Equity */}
              {groupedItems.equity?.length > 0 && (
                <>
                  <tr className="bg-purple-500/5">
                    <td colSpan={4} className="px-6 py-2 font-medium text-purple-700 dark:text-purple-400">
                      Equity
                    </td>
                  </tr>
                  {groupedItems.equity.map(item => (
                    <tr key={item.account_id} className="hover:bg-muted/30">
                      <td className="px-6 py-3 pl-10 font-mono text-sm">{item.account_code}</td>
                      <td className="px-6 py-3">{item.account_name}</td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                      </td>
                    </tr>
                  ))}
                </>
              )}

              {/* Income */}
              {groupedItems.income?.length > 0 && (
                <>
                  <tr className="bg-emerald-500/5">
                    <td colSpan={4} className="px-6 py-2 font-medium text-emerald-700 dark:text-emerald-400">
                      Income
                    </td>
                  </tr>
                  {groupedItems.income.map(item => (
                    <tr key={item.account_id} className="hover:bg-muted/30">
                      <td className="px-6 py-3 pl-10 font-mono text-sm">{item.account_code}</td>
                      <td className="px-6 py-3">{item.account_name}</td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                      </td>
                    </tr>
                  ))}
                </>
              )}

              {/* Expenses */}
              {groupedItems.expense?.length > 0 && (
                <>
                  <tr className="bg-amber-500/5">
                    <td colSpan={4} className="px-6 py-2 font-medium text-amber-700 dark:text-amber-400">
                      Expenses
                    </td>
                  </tr>
                  {groupedItems.expense.map(item => (
                    <tr key={item.account_id} className="hover:bg-muted/30">
                      <td className="px-6 py-3 pl-10 font-mono text-sm">{item.account_code}</td>
                      <td className="px-6 py-3">{item.account_name}</td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-mono">
                        {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
            <tfoot className="bg-muted/50 font-semibold">
              <tr>
                <td colSpan={2} className="px-6 py-4">Total</td>
                <td className="px-6 py-4 text-right font-mono">{formatCurrency(data?.total_debit || 0)}</td>
                <td className="px-6 py-4 text-right font-mono">{formatCurrency(data?.total_credit || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {(!data?.items || data.items.length === 0) && (
          <div className="text-center py-12">
            <Scales size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No transactions to display</p>
            <p className="text-sm text-muted-foreground mt-1">
              Post journal entries to see the trial balance
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
